import {
  REPORT_SHIFTS,
  REPORT_SHIFT_LABEL,
  WEATHER_CONDITIONS,
  WEATHER_CONDITION_LABEL,
  type ReportShift,
  type ShiftWeather,
  type WeatherCondition,
} from '@obraplan/core/domain/planning/daily-report-weather';
import { Input } from '@/components/ui/input';
import { useDraft } from '@/lib/use-draft';
import { ReportSection, SELECT_CLASS } from './DailyReportSection';

/**
 * O tempo e a jornada, turno a turno (RN-211, RN-215).
 *
 * ```text
 * Turno   Teve expediente  Início  Fim    Tempo     Deu para trabalhar
 * Manhã   [x]              07:00   12:00  Nublado   [x]
 * Tarde   [x]              13:00   17:00  Chuvoso   [ ]  ← sustenta prorrogação
 * Noite   [ ]                                            ← sem expediente: não sai no papel
 * ```
 *
 * Os três turnos estão sempre na tela, inclusive os que ninguém vai preencher. É mais
 * simples mandar o formulário inteiro do que decidir o que omitir, e quem separa o
 * relatado do vazio é o domínio (RN-211) — a noite em branco não é impressa.
 *
 * **Teve expediente nesse horário** separa o turno em que não se trabalha (a noite, quase
 * sempre) do turno em que se trabalharia e o tempo impediu. Antes de existir, a noite sem
 * expediente era desmarcada em "Deu para trabalhar" e o RDO pedia a condição do tempo que
 * impediu — que não houve (pedido do Gilvan, 30/10/2026). Sem expediente, o turno vai em
 * branco e o domínio o descarta (RN-211): não pede nada e não sai no papel. Nasce marcado
 * na manhã e na tarde e desmarcado na noite; no RDO já gravado, vale o que foi relatado.
 *
 * **Deu para trabalhar** começa marcado de propósito: o dia comum é o que se trabalhou, e
 * um formulário que exigisse marcar "sim" três vezes por dia ensinaria a marcar sem ler.
 * Desmarcá-lo é que é o ato — e ele exige dizer o que impediu (RN-215), porque é a outra
 * metade da frase que sustenta o pedido de prorrogação.
 */

interface LinhaDoTurno {
  readonly shift: ReportShift;
  /** Teve expediente nesse horário. Desmarcado, o turno vai em branco (RN-211). */
  readonly onDuty: boolean;
  readonly startTime: string;
  readonly endTime: string;
  readonly condition: '' | WeatherCondition;
  readonly workable: boolean;
}

function montar(gravados: readonly ShiftWeather[]): readonly LinhaDoTurno[] {
  const porTurno = new Map(gravados.map((turno) => [turno.shift, turno]));
  return REPORT_SHIFTS.map((shift) => {
    const gravado = porTurno.get(shift);
    return {
      shift,
      // Só o relatado é gravado (RN-211), então gravado é turno com expediente. O RDO que
      // ainda não tem turno nenhum nasce com a manhã e a tarde, e sem a noite.
      onDuty: gravado !== undefined || (gravados.length === 0 && shift !== 'NIGHT'),
      startTime: gravado?.startTime ?? '',
      endTime: gravado?.endTime ?? '',
      condition: gravado?.condition ?? '',
      // Turno que nunca foi relatado nasce praticável; o gravado vale como está.
      workable: gravado?.workable ?? true,
    };
  });
}

/**
 * O turno como o contrato o quer.
 *
 * A chave **ausente** é diferente da chave com `undefined`: `exactOptionalPropertyTypes`
 * distingue as duas, e um `undefined` explícito viraria `null` no `jsonb` — trazendo de
 * volta a diferença entre "não informado" e "informado como nada".
 */
export interface ShiftInput {
  readonly shift: ReportShift;
  readonly startTime?: string;
  readonly endTime?: string;
  readonly condition?: WeatherCondition;
  readonly workable: boolean;
}

function toShiftInput(linha: LinhaDoTurno): ShiftInput {
  // Sem expediente, o turno vai em branco, mesmo que a hora ou o tempo tenham ficado na
  // tela: é o domínio que o descarta (RN-211), e não pede a condição que impediu.
  if (!linha.onDuty) return { shift: linha.shift, workable: true };
  return {
    shift: linha.shift,
    workable: linha.workable,
    ...(linha.startTime === '' ? {} : { startTime: linha.startTime }),
    ...(linha.endTime === '' ? {} : { endTime: linha.endTime }),
    ...(linha.condition === '' ? {} : { condition: linha.condition }),
  };
}

export function DailyReportWeather({
  shifts,
  editable,
  isSaving,
  errorMessage,
  onSave,
}: {
  shifts: readonly ShiftWeather[];
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onSave: (shifts: readonly ShiftInput[]) => void;
}) {
  const [linhas, setLinhas] = useDraft(shifts, montar);

  const alterar = (shift: ReportShift, mudanca: Partial<LinhaDoTurno>): void => {
    setLinhas((atuais) =>
      atuais.map((linha) => (linha.shift === shift ? { ...linha, ...mudanca } : linha)),
    );
  };

  return (
    <ReportSection
      title="Tempo e jornada"
      description="Turno sem expediente não sai no papel. Com expediente, marcar que não deu para trabalhar exige dizer o que impediu."
      editable={editable}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onSave={() => {
        onSave(linhas.map(toShiftInput));
      }}
    >
      <div className="flex flex-col gap-3">
        {linhas.map((linha) => (
          <fieldset
            key={linha.shift}
            className="flex flex-wrap items-end gap-3 rounded-md border border-(--color-border-subtle) p-3"
          >
            <legend className="px-1 text-sm font-medium">{REPORT_SHIFT_LABEL[linha.shift]}</legend>

            <label className="flex min-h-11 basis-full items-center gap-2 text-sm md:min-h-9">
              <input
                type="checkbox"
                disabled={!editable}
                checked={linha.onDuty}
                onChange={(event) => {
                  alterar(linha.shift, { onDuty: event.target.checked });
                }}
              />
              Teve expediente nesse horário
            </label>

            {linha.onDuty && (
              <>
                <label className="flex flex-col gap-1 text-xs text-(--color-content-muted)">
                  Início
                  <Input
                    type="time"
                    className="w-32"
                    disabled={!editable}
                    value={linha.startTime}
                    onChange={(event) => {
                      alterar(linha.shift, { startTime: event.target.value });
                    }}
                  />
                </label>

                <label className="flex flex-col gap-1 text-xs text-(--color-content-muted)">
                  Fim
                  <Input
                    type="time"
                    className="w-32"
                    disabled={!editable}
                    value={linha.endTime}
                    onChange={(event) => {
                      alterar(linha.shift, { endTime: event.target.value });
                    }}
                  />
                </label>

                <label className="flex flex-col gap-1 text-xs text-(--color-content-muted)">
                  Tempo
                  <select
                    className={`${SELECT_CLASS} w-40`}
                    disabled={!editable}
                    value={linha.condition}
                    onChange={(event) => {
                      alterar(linha.shift, {
                        condition: event.target.value as '' | WeatherCondition,
                      });
                    }}
                  >
                    <option value="">—</option>
                    {WEATHER_CONDITIONS.map((condicao) => (
                      <option key={condicao} value={condicao}>
                        {WEATHER_CONDITION_LABEL[condicao]}
                      </option>
                    ))}
                  </select>
                </label>

                {/*
              O tempo e a consequência são dois campos (ADR-125): "choveu e trabalhou" e
              "choveu e parou" são dias diferentes, e é o segundo que sustenta o pedido de
              prorrogação. Numa lista única os dois viram a mesma linha.
            */}
                <label className="flex min-h-11 items-center gap-2 text-sm md:min-h-9">
                  <input
                    type="checkbox"
                    disabled={!editable}
                    checked={linha.workable}
                    onChange={(event) => {
                      alterar(linha.shift, { workable: event.target.checked });
                    }}
                  />
                  Deu para trabalhar
                </label>
              </>
            )}
          </fieldset>
        ))}
      </div>
    </ReportSection>
  );
}
